/**
 * Client-side photo compression.
 *
 * A 12MP phone photo is ~4MB. A tech in a basement can shoot 40 of them
 * before they get signal back — that's a 160MB sync. We downscale to a
 * max edge of 1600px and re-encode as JPEG q0.6, which lands around
 * 200-400KB per photo while staying perfectly readable for a report.
 *
 * Falls back to the original file if anything about the canvas path
 * fails (very old browser, corrupt EXIF, OOM on a huge panorama) —
 * losing the photo would be far worse than a big upload.
 */
const MAX_EDGE = 1600;
const QUALITY = 0.6;

export async function compressImage(file, { maxEdge = MAX_EDGE, quality = QUALITY } = {}) {
  if (!file || !file.type?.startsWith("image/")) return file;
  try {
    const bitmap = await loadBitmap(file);
    const scale = Math.min(1, maxEdge / Math.max(bitmap.width, bitmap.height));
    const w = Math.round(bitmap.width * scale);
    const h = Math.round(bitmap.height * scale);
    const canvas = document.createElement("canvas");
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext("2d");
    ctx.drawImage(bitmap, 0, 0, w, h);
    if (bitmap.close) bitmap.close();
    const blob = await new Promise((resolve) =>
      canvas.toBlob(resolve, "image/jpeg", quality)
    );
    if (!blob || blob.size >= file.size) return file;
    return new File([blob], renameToJpg(file.name), { type: "image/jpeg" });
  } catch {
    return file;
  }
}

function renameToJpg(name = "photo") {
  return `${name.replace(/\.[^.]+$/, "")}.jpg`;
}

async function loadBitmap(file) {
  if (typeof createImageBitmap === "function") {
    return await createImageBitmap(file);
  }
  return await new Promise((resolve, reject) => {
    const img = new Image();
    const url = URL.createObjectURL(file);
    img.onload = () => { URL.revokeObjectURL(url); resolve(img); };
    img.onerror = (e) => { URL.revokeObjectURL(url); reject(e); };
    img.src = url;
  });
}
